import React from 'react';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthFlowShell, AuthFlowHeader, AuthFlowFooter } from '@layouts/AuthFlowShell';
import type { AuthFlowPageId } from '@layouts/AuthFlowShell';
import { useAuth } from '@contexts/AuthContext';
import { useEffect, useRef } from 'react';
import { signUpTransition } from '@utils/motionConfig';
import { authLogout } from '@services/api';
import { clearSignupOtpSession } from '@utils/signupOtpSession';
import { PageSkeleton } from '@components/PageSkeleton';
import heroImage from '../../public/images/hero-image.webp';

const LAST_SEGMENT_KEY = 'saee:lastPathSegment';

const VALID_WIZARD_PAGES = new Set<AuthFlowPageId>([
  'signup',
  'signup-verify',
  'signup-complete',
  'nafath-verification',
  'select-national-address',
  'success',
]);

/**
 * Layout for the broker signup wizard. Wraps all wizard steps in AuthFlowShell
 * with AnimatePresence transitions. Computes the back-button handler per step.
 * Also handles redirect of already-authenticated users back to their dashboard.
 */
export function SignupWizardLayout() {
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { authLoading, isAuthenticated, profileCompleted, clearSession } = useAuth();
  const redirectFired = useRef(false);

  const l = lang === 'ar' ? 'ar' : 'en';
  const currentPage = (pathname.split('/').pop() ?? 'signup') as AuthFlowPageId;
  const isValidPage = VALID_WIZARD_PAGES.has(currentPage);

  // Redirect already-authenticated broker landing on /signup back to their dashboard.
  // Must run before any conditional return to satisfy rules-of-hooks.
  useEffect(() => {
    if (!isValidPage) return;
    if (authLoading || !isAuthenticated || redirectFired.current) return;
    if (currentPage !== 'signup') return;
    redirectFired.current = true;
    if (profileCompleted === false) {
      navigate(`/${l}/signup-complete`, { replace: true });
      return;
    }
    if (profileCompleted !== true) return;
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(LAST_SEGMENT_KEY) : null;
    const seg = stored && /^[a-z0-9-]+$/.test(stored) ? stored : 'main-brokerage';
    navigate(`/${l}/${seg}`, { replace: true });
  }, [isValidPage, authLoading, isAuthenticated, profileCompleted, currentPage, l, navigate]);

  if (!isValidPage) {
    return <Navigate to={`/${l}/signup`} replace />;
  }

  const onBack = currentPage === 'signup' ? undefined : () => {
    if (currentPage === 'signup-verify') {
      clearSignupOtpSession();
      void authLogout().catch(() => {}).finally(() => {
        clearSession();
        navigate(`/${l}/signup`, { replace: true });
      });
      return;
    }
    if (currentPage === 'signup-complete') {
      void authLogout().catch(() => {}).finally(() => {
        clearSession();
        navigate(`/${l}/signup`, { replace: true });
      });
      return;
    }
    if (currentPage === 'nafath-verification') { navigate(`/${l}/signup-complete`); return; }
    if (currentPage === 'select-national-address') { navigate(`/${l}/nafath-verification`); return; }
    if (currentPage === 'success') { navigate(`/${l}/select-national-address`); }
  };

  return (
    <AuthFlowShell
      header={<AuthFlowHeader currentPage={currentPage} onBack={onBack} />}
      footer={<AuthFlowFooter />}
      heroImage={heroImage}
    >
      <div className="flex min-h-0 w-full flex-col items-center justify-center overflow-visible py-0">
        <div className="w-full max-w-[calc(28rem+3rem)] overflow-visible sm:max-w-[calc(28rem+4rem)]">
          <React.Suspense fallback={<PageSkeleton variant="auth" />}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={pathname}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: signUpTransition.duration, ease: signUpTransition.ease }}
                className="w-full max-w-md mx-auto flex flex-col items-center"
                style={{ willChange: 'transform' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </React.Suspense>
        </div>
      </div>
    </AuthFlowShell>
  );
}
